import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Box, Container, Typography, Avatar, Chip, Paper, IconButton,} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import {
  ArrowBack,
  Notifications,
  CheckCircle,
  Info,
  Warning,
  Error,
  Celebration,
  

} from "@mui/icons-material";
import { useNotification } from "../notification_context";
import React, { useEffect } from "react";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: { main: "#0288D1", light: "#e0f2fe", dark: "#0277bd" },
    background: { default: "#F0F6FF", paper: "#ffffff" },
    text: { primary: "#0c1a2e", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Bricolage Grotesque', 'Sora', sans-serif",
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;


// ─── Notification type config ─────────────────────────────────────────────────
const TYPE_CONFIG = {
  success: {
    icon: CheckCircle,
    color: "#059669",
    bg: alpha("#059669", 0.1),
    border: alpha("#059669", 0.2),
  },
  info: {
    icon: Info,
    color: "#0288D1",
    bg: alpha("#0288D1", 0.1),
    border: alpha("#0288D1", 0.2),
  },
  warning: {
    icon: Warning,
    color: "#D97706",
    bg: alpha("#D97706", 0.1),
    border: alpha("#D97706", 0.2),
  },
  error: {
    icon: Error,
    color: "#DC2626",
    bg: alpha("#DC2626", 0.08),
    border: alpha("#DC2626", 0.18),
  },
  celebration: {
    icon: Celebration,
    color: "#7C3AED",
    bg: alpha("#7C3AED", 0.1),
    border: alpha("#7C3AED", 0.2),
  },
  default: {
    icon: Notifications,
    color: "#0288D1",
    bg: alpha("#0288D1", 0.1),
    border: alpha("#0288D1", 0.2),
  },
};

// ─── Styled Components ────────────────────────────────────────────────────────
const HeroBar = styled(Box)(() => ({
  background: "linear-gradient(135deg, #023e8a 0%, #0288D1 55%, #0ea5e9 100%)",
  padding: "72px 24px 100px",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    opacity: 0.05,
    backgroundImage:
      "radial-gradient(circle at 25% 50%, #fff 1px, transparent 1px), radial-gradient(circle at 75% 50%, #fff 1px, transparent 1px)",
    backgroundSize: "55px 55px",
  },
}));

const HeroBackBtn = styled(IconButton)(() => ({
  position: "absolute",
  left: 20,
  top: 20,
  width: 44,
  height: 44,
  borderRadius: 12,
  background: "rgba(255,255,255,0.15)",
  border: "1px solid rgba(255,255,255,0.25)",
  color: "#fff",
  zIndex: 5,
  transition: "all 0.2s ease",

  "&:hover": {
    background: "rgba(255,255,255,0.28)",
  },

  "&:active": {
    transform: "scale(0.95)",
  },
}));



const ListCard = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 12px 36px rgba(2,136,209,0.06)",
  overflow: "hidden",
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: "80ms",
}));

const NotifRow = styled(Box, {
  shouldForwardProp: (p) => p !== "unread" && p !== "itemdelay",
})(({ theme, unread, itemdelay }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 16,
  padding: "18px 24px",
  borderBottom: `1px solid ${alpha("#000", 0.05)}`,
  background: unread ? alpha(theme.palette.primary.main, 0.025) : "#fff",
  animation: `${cardEntrance} 0.4s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.2s ease",
  cursor: "default",
  position: "relative",
  "&:last-child": { borderBottom: "none" },
  "&:hover": {
    background: unread
      ? alpha(theme.palette.primary.main, 0.04)
      : alpha(theme.palette.primary.main, 0.02),
  },
}));

const UnreadDot = styled(Box)(() => ({
  position: "absolute",
  top: 20,
  right: 22,
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: "#0288D1",
  boxShadow: "0 0 0 3px rgba(2,136,209,0.2)",
}));

const NotifAvatar = styled(Avatar, {
  shouldForwardProp: (p) =>
    p !== "notifcolor" && p !== "notifbg" && p !== "notifborder",
})(({ notifcolor, notifbg, notifborder }) => ({
  width: 46,
  height: 46,
  borderRadius: 13,
  flexShrink: 0,
  background: notifbg,
  border: `1.5px solid ${notifborder}`,
  color: notifcolor,
  "& svg": { fontSize: 22 },
}));

const SkeletonCard = styled(Box)(() => ({
  borderRadius: 20,
  border: `1px solid ${alpha("#000", 0.07)}`,
  overflow: "hidden",
  background: "#fff",
}));

const SkeletonRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "18px 24px",
  borderBottom: `1px solid ${alpha("#000", 0.05)}`,
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
  "&:last-child": { borderBottom: "none" },
}));

const Sb = ({ w = "100%", h = 14, r = 6, delay = 0 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: r,
      background: alpha("#000", 0.07),
      flexShrink: 0,
      animationDelay: `${delay}ms`,
    }}
  />
);

const EmptyBox = styled(Paper)(({ theme }) => ({
  textAlign: "center",
  padding: "64px 32px",
  borderRadius: 20,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 12px 36px rgba(2,136,209,0.05)",
  animation: `${fadeUp} 0.5s ease both`,
}));

// ─── Main Component ───────────────────────────────────────────────────────────
const NotificationsPage = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAllAsRead, markingRead } =
    useNotification();

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };  

  const getConfig = (type) =>
    TYPE_CONFIG[type?.toLowerCase()] || TYPE_CONFIG.default;

  useEffect(() => {
    if (unreadCount > 0 && !markingRead) {
      markAllAsRead();
    }
  }, [unreadCount, markingRead, markAllAsRead]);

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&display=swap"
        rel="stylesheet"
      />

      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(160deg, #f0f6ff 0%, #e8f4fd 50%, #f5faff 100%)",
          fontFamily: "'Bricolage Grotesque', sans-serif",
        }}
      >
        {/* ── Hero ── */}
        <HeroBar>
          <HeroBackBtn
            onClick={handleGoBack}
            aria-label="Go back"
          >
            <ArrowBack sx={{ fontSize: 22 }} />
          </HeroBackBtn>

          {/* Decorative blobs */}
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -40,
              left: -40,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.03)",
              pointerEvents: "none",
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Chip
              label="🔔 Activity Center"
              size="small"
              sx={{
                mb: 2.5,
                fontWeight: 700,
                fontSize: "0.74rem",
                fontFamily: "'Bricolage Grotesque', sans-serif",
                letterSpacing: "0.4px",
                background: "rgba(255,255,255,0.14)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.22)",
                borderRadius: "8px",
                backdropFilter: "blur(8px)",
              }}
            />
            <Typography
              sx={{
                fontWeight: 900,
                color: "#fff",
                fontSize: { xs: "2.6rem", md: "4rem" },
                letterSpacing: "-1.5px",
                lineHeight: 1.08,
                fontFamily: "'Bricolage Grotesque', sans-serif",
                mb: 1.5,
              }}
            >
              Notifications
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "1.02rem",
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontWeight: 400,
                mb: unreadCount > 0 ? 3.5 : 0,
              }}
            >
              Stay updated with your latest activity
            </Typography>
            {/* // Update the MarkAllBtn in the hero */}
          </Box>
        </HeroBar>

        <Container
          maxWidth="md"
          sx={{
            py: { xs: 4, md: 6 },
            mt: { xs: -4, md: -5 },
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* ── Loading skeleton ── */}
          {loading && (
            <SkeletonCard>
              {[...Array(5)].map((_, i) => (
                <SkeletonRow key={i} sx={{ animationDelay: `${i * 90}ms` }}>
                  <Sb w={46} h={46} r={13} />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <Sb w="45%" h={15} delay={i * 90} />
                    <Sb w="75%" h={12} delay={i * 90 + 40} />
                    <Sb w="30%" h={10} delay={i * 90 + 80} />
                  </Box>
                </SkeletonRow>
              ))}
            </SkeletonCard>
          )}

          {/* ── Empty state ── */}
          {!loading && notifications.length === 0 && (
            <EmptyBox elevation={0}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: alpha("#0288D1", 0.08),
                  border: `1.5px solid ${alpha("#0288D1", 0.14)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                }}
              >
                <Notifications
                  sx={{ fontSize: 32, color: "#0288D1", opacity: 0.45 }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "1.15rem",
                  fontWeight: 800,
                  color: "#0c1a2e",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  mb: 0.8,
                  letterSpacing: "-0.2px",
                }}
              >
                All caught up!
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#9CA3AF",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontWeight: 400,
                }}
              >
                No new notifications right now. Check back later!
              </Typography>
            </EmptyBox>
          )}

          {/* ── Notifications list ── */}
          {!loading && notifications.length > 0 && (
            <>
              {/* Count row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  animation: `${fadeUp} .4s ease both`,
                  px: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#9CA3AF",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    textTransform: "uppercase",
                    letterSpacing: "0.7px",
                  }}
                >
                  {notifications.length} notification
                  {notifications.length !== 1 ? "s" : ""}
                  {unreadCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1.5,
                        background: alpha("#0288D1", 0.1),
                        color: "#0288D1",
                        borderRadius: "6px",
                        px: 1,
                        py: 0.3,
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    >
                      {unreadCount} unread
                    </Box>
                  )}
                </Typography>
              </Box>

              <ListCard elevation={0}>
                {notifications.map((notif, index) => {
                  const cfg = getConfig(notif.type);
                  const IconComp = cfg.icon;
                  const isUnread = !notif.is_read;

                  return (
                    <NotifRow
                      key={notif.id || index}
                      unread={isUnread ? 1 : 0}
                      itemdelay={index * 55}
                    >
                      {/* Avatar */}
                      <NotifAvatar
                        notifcolor={cfg.color}
                        notifbg={cfg.bg}
                        notifborder={cfg.border}
                      >
                        <IconComp />
                      </NotifAvatar>

                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0, pr: isUnread ? 3 : 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 800,
                            color: "#0c1a2e",
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            letterSpacing: "-0.1px",
                            lineHeight: 1.3,
                            mb: 0.5,
                          }}
                        >
                          {notif.title || "Notification"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#4B5563",
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            fontWeight: 400,
                            lineHeight: 1.65,
                            mb: 1,
                          }}
                        >
                          {notif.message}
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "#9CA3AF",
                              fontFamily: "'Bricolage Grotesque', sans-serif",
                              fontWeight: 600,
                            }}
                          >
                            {moment(notif.created_at).fromNow()}
                          </Typography>
                          {notif.type && (
                            <Box
                              sx={{
                                width: 3,
                                height: 3,
                                borderRadius: "50%",
                                background: "#D1D5DB",
                              }}
                            />
                          )}
                          {notif.type && (
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: cfg.color,
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                fontWeight: 700,
                                textTransform: "capitalize",
                              }}
                            >
                              {notif.type}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Unread indicator */}
                      {isUnread && <UnreadDot />}
                    </NotifRow>
                  );
                })}
              </ListCard>
            </>
          )}
        </Container>

        <Box sx={{ py: 6 }} />
      </Box>
    </ThemeProvider>
  );
};

export default NotificationsPage;
